import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyCopyPatch, pickFieldsFromData } from "../../../../helper/copyFormSections";

/**
 * Panel to copy shared sections from another registrant draft into the current form.
 * @param {{ sources: Array<{ index: number, label: string, data: object }>, sections: Array, setValue: Function }} props
 */
const CopyFromPanel = ({ sources = [], sections = [], setValue }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sourceIndex, setSourceIndex] = useState(sources[0]?.index ?? "");
  const [selected, setSelected] = useState(() => sections.map((s) => s.id));
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!sources.length) {
      setOpen(false);
      return;
    }
    if (!sources.some((s) => s.index === sourceIndex)) {
      setSourceIndex(sources[0].index);
    }
  }, [sources, sourceIndex]);

  const source = useMemo(
    () => sources.find((s) => s.index === sourceIndex) || null,
    [sources, sourceIndex]
  );

  if (!sources.length) return null;

  const toggleSection = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => setSelected(sections.map((s) => s.id));
  const clearAll = () => setSelected([]);

  const handleApply = () => {
    if (!source || !selected.length) return;
    const patch = pickFieldsFromData(source.data, sections, selected);
    applyCopyPatch(setValue, patch);
    setFeedback(t("_copy_applied"));
    setTimeout(() => setFeedback(""), 2500);
  };

  return (
    <div
      className="mb-4"
      data-testid="copy-from-panel"
      style={{
        background: "#f4f7fb",
        border: "1px solid #c5d0e0",
        borderRadius: "8px",
        padding: "14px 16px",
      }}
    >
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <div className="fw-semibold" style={{ color: "#032C65" }}>
            {t("_copy_from_title")}
          </div>
          <small className="text-muted">{t("_copy_from_hint")}</small>
        </div>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="copy-from-toggle"
          onClick={() => setOpen((v) => !v)}
          style={{
            border: "1px solid #032C65",
            color: "#032C65",
            background: open ? "#e8f0fb" : "#fff",
            fontWeight: 600,
          }}
        >
          {open ? t("_copy_from_hide") : t("_copy_from_show")}
        </button>
      </div>

      {open && (
        <div className="mt-3">
          <label className="form-label fw-semibold mb-1">{t("_copy_from_source")}</label>
          <select
            className="form-select border mb-3"
            value={sourceIndex}
            onChange={(e) => setSourceIndex(Number(e.target.value))}
            data-testid="copy-from-source"
          >
            {sources.map((s) => (
              <option key={s.index} value={s.index}>
                {s.label}
              </option>
            ))}
          </select>

          <div className="d-flex flex-wrap gap-2 mb-2">
            <button type="button" className="btn btn-link btn-sm p-0" onClick={selectAll}>
              {t("_copy_select_all")}
            </button>
            <span className="text-muted">·</span>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={clearAll}>
              {t("_copy_clear_all")}
            </button>
          </div>

          <div className="row g-2 mb-3">
            {sections.map((section) => (
              <div className="col-sm-6" key={section.id}>
                <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(section.id)}
                    onChange={() => toggleSection(section.id)}
                    data-testid={`copy-section-${section.id}`}
                  />
                  <span>{t(section.labelKey)}</span>
                </label>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="sigma_btn-custom light"
            style={{ padding: "10px 18px" }}
            disabled={!selected.length}
            onClick={handleApply}
            data-testid="copy-from-apply"
          >
            {t("_copy_apply")}
          </button>
          {feedback && (
            <span className="ms-3 text-success fw-semibold" data-testid="copy-from-feedback">
              {feedback}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CopyFromPanel;
